import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
import PregnancyEducationalContent from "./PregnancyEducationalContent";
const TITLE = `Calculatrice de grossesse - Calculez la date d'accouchement avec précision`;
const DESCRIPTION =
  "Calculatrice de grossesse - Calculez la date d pour obtenir des estimations utiles en sante feminine et suivi du cycle. Entrez vos informations et recevez des resultats rapides pour mieux planifier.";
const KEYWORDS =
  "de grossesse la date d, calculatrice de grossesse la date d, calcul en ligne, sante feminine, cycle, grossesse, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/pregnancy/";

const GREGORIAN_MONTHS = [
  "janvier", "février", "mars", "avril",
  "mai", "juin", "juillet", "août",
  "septembre", "octobre", "novembre", "décembre"
];

function PregnancyCalculator() {
  const [dateInput, setDateInput] = useState({
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const [cycleLength, setCycleLength] = useState(28);
  const [lastPeriodError, setLastPeriodError] = useState("");
  const [cycleLengthError, setCycleLengthError] = useState("");
  const [result, setResult] = useState(null);

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const getDayOfWeek = (date) => {
    const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    return days[date.getDay()];
  };

  const getSeason = (month) => {
    if (month >= 3 && month <= 5) return "Printemps";
    if (month >= 6 && month <= 8) return "Été";
    if (month >= 9 && month <= 11) return "Automne";
    return "Hiver";
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculateDaysBetween = (date1, date2) => {
    const diffTime = Math.abs(date2 - date1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const newDateInput = { ...dateInput, [name]: value };
    setDateInput(newDateInput);

    const selectedDate = new Date(newDateInput.year, newDateInput.month - 1, newDateInput.day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      setLastPeriodError("La date doit être dans le passé");
    } else {
      const diffDays = calculateDaysBetween(selectedDate, today);
      if (diffDays > 365) {
        setLastPeriodError("La date semble trop loin dans le passé");
      } else {
        setLastPeriodError("");
      }
    }
  };

  const handleCycleLengthChange = (event) => {
    const value = event.target.value;
    if (value !== "") {
      const parsedValue = parseInt(value);
      if (!isNaN(parsedValue) && parsedValue >= 20 && parsedValue <= 45) {
        setCycleLength(parsedValue);
        setCycleLengthError("");
      } else {
        setCycleLength(value);
        if (isNaN(parsedValue)) {
          setCycleLengthError("Veuillez entrer un nombre valide");
        } else if (parsedValue < 20) {
          setCycleLengthError("La durée du cycle doit être au moins 20 jours");
        } else if (parsedValue > 45) {
          setCycleLengthError("La durée du cycle ne doit pas dépasser 45 jours");
        }
      }
    } else {
      setCycleLength("");
      setCycleLengthError("");
    }
  };

  const handleReset = () => {
    setDateInput({
      day: new Date().getDate(),
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });
    setCycleLength(28);
    setLastPeriodError("");
    setCycleLengthError("");
    setResult(null);
  };

  const calculatePregnancy = (e) => {
    e.preventDefault();
    const lmpDate = new Date(dateInput.year, dateInput.month - 1, dateInput.day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isNaN(lmpDate.getTime()) || lastPeriodError) return;

    const conceptionDate = addDays(lmpDate, cycleLength - 14);
    const dueDate = addDays(lmpDate, 280);
    const daysSinceLMP = calculateDaysBetween(lmpDate, today);
    const weeksSinceLMP = Math.floor(daysSinceLMP / 7);
    const remainingDays = daysSinceLMP % 7;
    const daysUntilDue = calculateDaysBetween(today, dueDate);

    let trimester = weeksSinceLMP < 13 ? "Premier trimestre" : weeksSinceLMP < 27 ? "Deuxième trimestre" : "Troisième trimestre";

    setResult({
      lmpDate: formatDate(lmpDate),
      conceptionDate: formatDate(conceptionDate),
      dueDate: formatDate(dueDate),
      dueDateDay: getDayOfWeek(dueDate),
      dueDateSeason: getSeason(dueDate.getMonth()),
      currentWeek: weeksSinceLMP,
      currentDay: remainingDays,
      currentMonth: Math.floor(weeksSinceLMP / 4) + 1,
      trimester: trimester,
      daysUntilDue: daysUntilDue,
      totalDays: daysSinceLMP
    });
  };

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Calculatrice de grossesse</strong> est un outil médical gratuit et précis pour calculer
                <strong> la date prévue d'accouchement</strong> et déterminer
                <strong> l'âge de la grossesse</strong> en semaines et mois. Tout ce que vous devez faire est d'entrer la date du premier jour de vos dernières menstruations pour obtenir des informations complètes sur votre grossesse.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    Calculez votre date d'accouchement
                  </h2>
                </div>
                <form onSubmit={calculatePregnancy}>
                  <div className="form-group mb-3">
                    <label ><strong>Premier jour des dernières menstruations:</strong></label>
                    <div className="d-flex gap-2">
                      <select style={{width:'100px'}} className="form-control" name="day" value={dateInput.day} onChange={handleDropdownChange}>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <select style={{width:'100%'}} className="form-control" name="month" value={dateInput.month} onChange={handleDropdownChange}>
                        {GREGORIAN_MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>)}
                      </select>
                      <select style={{width:'100px'}} className="form-control" name="year" value={dateInput.year} onChange={handleDropdownChange}>
                        {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    {lastPeriodError && <div className="text-danger mt-1" style={{ fontSize: "14px" }}>{lastPeriodError}</div>}
                  </div>

                  <div className="form-group mb-4">
                    <label ><strong>Durée moyenne du cycle menstruel (jours):</strong></label>
                    <input type="number" min="20" max="45" value={cycleLength} onChange={handleCycleLengthChange} className="form-control" placeholder="28" />
                    {cycleLengthError && <div className="text-danger mt-1" style={{ fontSize: "14px" }}>{cycleLengthError}</div>}
                  </div>

                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-success btn-lg flex-grow-1">
                      Calculer
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={handleReset}>
                      Réinitialiser
                    </button>
                  </div>
                </form>
              </div>

              <div className="col-sm-12 col-md-6">
                {result && (
                  <div className="bg-light p-4 rounded mt-md-0 mt-3">
                    <h3 className="text-center mb-4">Résultats</h3>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <td><strong>Date d'accouchement prévue:</strong></td>
                          <td>{result.dueDate} ({result.dueDateDay})</td>
                        </tr>
                        <tr>
                          <td><strong>Semaines de grossesse actuelles:</strong></td>
                          <td>{result.currentWeek} semaines et {result.currentDay} jours</td>
                        </tr>
                        <tr>
                          <td><strong>Trimestre:</strong></td>
                          <td>{result.trimester}</td>
                        </tr>
                        <tr>
                          <td><strong>Jours jusqu'à l'accouchement:</strong></td>
                          <td>{result.daysUntilDue} jours</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12 col-md-8">
          <PregnancyEducationalContent />
        </div>
      </div>
      <div className="row mt-4">
        <div className="col-12 col-md-8">
          <SecondAds />
        </div>
      </div>
    </div>
  );
};
export default PregnancyCalculator;